import type { Answer, Quiz } from '@efdigital/ll-addon-data';
import {
  findCoachingQuiz,
  queryCoachingQuizAnswer,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-quiz';
import type {
  CoacheeEngagement,
  CoachingCoachCoacheesCoacheeModel,
} from '@efdigital/ll-xceed-v2/routes/coaching/coach/coachees/coachee';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

export type OnboardingAnswer = {
  quiz: Quiz;
  answer: Answer;
};

export type CoachingCoachCoacheesCoacheeAboutModel =
  CoachingCoachCoacheesCoacheeModel & {
    onboardingAnswers: OnboardingAnswer[];
  };

export default class CoachingCoachCoacheesCoacheeAboutRoute extends Route<CoachingCoachCoacheesCoacheeAboutModel> {
  @service declare store: Store;

  async model(): Promise<CoachingCoachCoacheesCoacheeAboutModel> {
    const parent = this.modelFor(
      'coaching.coach.coachees.coachee'
    ) as CoachingCoachCoacheesCoacheeModel;

    const quizIds = [
      ...new Set(
        parent.engagements
          .map((engagement: CoacheeEngagement) => engagement.coaching.quiz?.id)
          .filter((id): id is string => Boolean(id))
      ),
    ];

    const onboardingAnswers = (
      await Promise.all(
        quizIds.map((quizId) =>
          this.loadPublishedAnswer(quizId, parent.coachee.coacheeUserId)
        )
      )
    ).filter((entry): entry is OnboardingAnswer => Boolean(entry));

    return {
      ...parent,
      onboardingAnswers,
    };
  }

  private async loadPublishedAnswer(
    quizId: string,
    coacheeUserId: string
  ): Promise<OnboardingAnswer | undefined> {
    const queryQuiz = await this.store.request(findCoachingQuiz(quizId));
    const quiz = queryQuiz.content.data;
    if (!quiz) {
      return undefined;
    }

    const queryAnswers = await this.store.request(
      queryCoachingQuizAnswer(coacheeUserId, quizId)
    );
    const answer = queryAnswers.content.data?.[0];
    if (!answer || answer.draft) {
      return undefined;
    }

    return { quiz, answer };
  }
}
